import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppAuth } from "@/auth/use-app-auth";
import { Menu, X, ChevronDown, ChevronRight, LayoutDashboard, Loader2, LogOut } from "lucide-react";
import { useUsage } from "@/hooks/useUsage";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/custom/logo";
import { useAuthFlow, getOrgFromLocation } from "@/lib/auth-flow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const HomeWordmark = () => (
  <div className="home-wordmark flex items-center gap-3">
    <img src="/logo/theRecruitIcon.svg" alt="" className="home-wordmark__icon" />
    <span className="home-wordmark__text">
      <span className="home-wordmark__prefix">the</span>
      <span className="home-wordmark__brand">Recruit</span>
      <span className="home-wordmark__dot">.</span>
    </span>
  </div>
);

export const GlobalHeader: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const [resourcesMenuOpen, setResourcesMenuOpen] = useState(false);
  const [industriesMenuOpen, setIndustriesMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated, isLoading, logout } = useAppAuth();
  const startAuth = useAuthFlow();
  const { toast } = useToast();
  const { planName } = useUsage();
  const orgAuthId = getOrgFromLocation();

  const isHomePage = pathname === "/" || pathname === "/home";
  const isTechnicalMarketingPage = [
    "/",
    "/home",
    "/demo",
    "/pricing",
    "/blog",
    "/changelog",
    "/contact",
    "/for-legal",
  ].includes(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu on route change
    setOpen(false);
    setProductMenuOpen(false);
    setResourcesMenuOpen(false);
    setIndustriesMenuOpen(false);
  }, [pathname]);

  const handleLogin = async () => {
    await startAuth({
      mode: "login",
      returnTo: "/dashboard",
      orgAuthId,
    });
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    try {
      await logout({
        logoutParams: {
          returnTo: `${window.location.origin}/`,
        },
      });
    } catch (error) {
      console.error("Failed to log out from the global header", error);
      setIsLoggingOut(false);
      toast({
        variant: "destructive",
        title: "Couldn't log out",
        description: "Your session is still active. Please try again.",
      });
    }
  };

  const scrollToSection = (sectionId: string) => {
    if (!isHomePage) {
      navigate(`/#${sectionId}`);
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      element.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
    }
    setOpen(false);
    setProductMenuOpen(false);
    setResourcesMenuOpen(false);
  };

  const navLinkClasses =
    "relative text-amber-900 hover:text-amber-950 transition-colors duration-200 font-medium after:absolute after:left-0 after:bottom-[-4px] after:h-[2px] after:w-0 after:bg-amber-500 after:transition-all after:duration-300 hover:after:w-full";

  const productMenuItems = [
    {
      id: "semantic-search",
      label: "Semantic Talent Search",
      desc: "Find the person behind the resume",
    },
    {
      id: "spotlight-intros",
      label: "Spotlight Intros",
      desc: "Meet candidates before the first call",
    },
    {
      id: "bulk-interviews",
      label: "Bulk AI Interviews",
      desc: "Invite candidates to role-specific AI interviews",
    },
    { id: "citation-ai", label: "Citation AI", desc: "Evidence-backed hiring decisions" },
  ];

  const resourcesMenuItems = [
    {
      label: "Our approach",
      desc: "More context, with human judgment",
      type: "section" as const,
      id: "testimonials",
    },
    {
      label: "Blog",
      desc: "Editorial thinking on AI recruiting",
      type: "link" as const,
      to: "/blog",
    },
    {
      label: "Changelog",
      desc: "Track every shipped improvement",
      type: "link" as const,
      to: "/changelog",
    },
    { label: "Contact", desc: "Talk to the team", type: "link" as const, to: "/contact" },
  ];

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-40 transition-all duration-300",
        isTechnicalMarketingPage && "technical-nav",
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      )}
      role="banner"
    >
      {/* Candidate cross-link banner */}
      <div
        className="w-full border-b"
        style={{
          background: "rgba(212,168,83,0.07)",
          borderColor: "rgba(212,168,83,0.18)",
        }}
      >
        <div className="flex items-center justify-center gap-1.5 py-1.5 text-[11px]">
          <span style={{ color: "rgba(27,28,25,0.70)" }}>Are you a candidate?</span>
          <a
            href="https://firstround.com.au"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold transition-colors hover:opacity-75"
            style={{ color: "#7b5804" }}
          >
            Check out firstround.com.au
          </a>
        </div>
      </div>

      <nav
        className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-18 sm:px-6 lg:px-8"
        aria-label="Global"
      >
        {/* Logo */}
        <div className="flex items-center gap-3">
          <Link to="/" aria-label="theRecruit home" className="flex items-center gap-2 group">
            {isTechnicalMarketingPage ? <HomeWordmark /> : <Logo theme="light" size={40} />}
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden xl:flex items-center gap-8 text-sm">
          {/* Product Dropdown */}
          <div className="relative">
            <button
              onClick={() => setProductMenuOpen(!productMenuOpen)}
              onMouseEnter={() => setProductMenuOpen(true)}
              className={cn(navLinkClasses, "flex items-center gap-1")}
            >
              Product
              <ChevronDown
                className={cn("w-4 h-4 transition-transform", productMenuOpen && "rotate-180")}
              />
            </button>

            {productMenuOpen && (
              <div
                className="absolute top-full left-0 mt-2 w-72 rounded-2xl bg-white border border-slate-200 shadow-xl p-2"
                onMouseLeave={() => setProductMenuOpen(false)}
              >
                {productMenuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="w-full text-left px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors group"
                  >
                    <div className="font-medium text-slate-900 group-hover:text-amber-600 transition-colors">
                      {item.label}
                    </div>
                    <div className="text-sm text-slate-500">{item.desc}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => scrollToSection("crisis")} className={navLinkClasses}>
            Why theRecruit
          </button>
          <DropdownMenu open={industriesMenuOpen} onOpenChange={setIndustriesMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button type="button" className={cn(navLinkClasses, "flex items-center gap-1")}>
                By Industry
                <ChevronDown
                  className={cn("h-4 w-4 transition-transform", industriesMenuOpen && "rotate-180")}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={8}
              className="w-64 rounded-2xl border-slate-200 bg-white p-2 shadow-xl"
            >
              <DropdownMenuItem asChild>
                <Link
                  to="/for-legal"
                  aria-current={pathname === "/for-legal" ? "page" : undefined}
                  className="flex cursor-pointer flex-col items-start gap-1 rounded-xl px-4 py-3 text-slate-900 focus:bg-slate-50 focus:text-amber-800"
                >
                  <span className="font-medium">Legal</span>
                  <span className="text-sm text-slate-600">Support your legal hiring team</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="flex flex-col items-start gap-1 px-4 py-3">
                <span className="font-medium">Hospitality</span>
                <span className="text-sm">Coming soon</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/pricing" className={navLinkClasses}>
            Pricing
          </Link>
          <Link to="/demo" className={navLinkClasses}>
            Demo Hub
          </Link>

          <div className="relative">
            <button
              onClick={() => setResourcesMenuOpen(!resourcesMenuOpen)}
              onMouseEnter={() => setResourcesMenuOpen(true)}
              className={cn(navLinkClasses, "flex items-center gap-1")}
            >
              Resources
              <ChevronDown
                className={cn("w-4 h-4 transition-transform", resourcesMenuOpen && "rotate-180")}
              />
            </button>

            {resourcesMenuOpen && (
              <div
                className="absolute top-full left-0 mt-2 w-72 rounded-2xl bg-white border border-slate-200 shadow-xl p-2"
                onMouseLeave={() => setResourcesMenuOpen(false)}
              >
                {resourcesMenuItems.map((item) =>
                  item.type === "section" ? (
                    <button
                      key={item.label}
                      onClick={() => scrollToSection(item.id)}
                      className="w-full text-left px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors group"
                    >
                      <div className="font-medium text-slate-900 group-hover:text-amber-600 transition-colors">
                        {item.label}
                      </div>
                      <div className="text-sm text-slate-500">{item.desc}</div>
                    </button>
                  ) : (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={() => setResourcesMenuOpen(false)}
                      className="block w-full text-left px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors group"
                    >
                      <div className="font-medium text-slate-900 group-hover:text-amber-600 transition-colors">
                        {item.label}
                      </div>
                      <div className="text-sm text-slate-500">{item.desc}</div>
                    </Link>
                  )
                )}
              </div>
            )}
          </div>
        </div>

        {/* Desktop Actions */}
        <div className="hidden xl:flex items-center gap-3">
          {!isLoading && !isAuthenticated ? (
            <button
              type="button"
              onClick={handleLogin}
              aria-label="Start hiring free with theRecruit"
              className="group flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 transition-all duration-300 hover:border-amber-400 hover:bg-amber-100 hover:text-amber-950"
            >
              Start Hiring Free
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          ) : null}
          {isLoading ? (
            <div
              className="h-10 w-24 animate-pulse rounded-full bg-slate-100"
              aria-hidden="true"
              data-testid="header-auth-loading"
            />
          ) : isAuthenticated ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="group flex min-h-10 items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                    aria-label="Open dashboard menu"
                  >
                    Dashboard
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={8}
                  className="w-52 border-slate-200 bg-white p-1.5 shadow-lg"
                >
                  <DropdownMenuItem asChild>
                    <Link
                      to="/dashboard"
                      className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-slate-700 focus:bg-slate-50 focus:text-slate-900"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Go to Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-1 bg-slate-100" />
                  <DropdownMenuItem
                    disabled={isLoggingOut}
                    onSelect={() => void handleLogout()}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium text-rose-600 focus:bg-rose-50 focus:text-rose-700 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60"
                  >
                    {isLoggingOut ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <LogOut className="h-4 w-4" />
                    )}
                    {isLoggingOut ? "Logging out..." : "Log out"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {planName ? (
                <span className="rounded-full bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-700">
                  {planName}
                </span>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleLogin}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
              >
                Login
              </button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="xl:hidden inline-flex items-center justify-center rounded-xl p-2.5 text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="Toggle navigation menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {open && (
        <div className="xl:hidden max-h-[calc(100dvh-104px)] overflow-y-auto border-t border-slate-200 bg-white/98 backdrop-blur-md">
          <div className="mx-auto max-w-7xl px-4 py-6">
            <div className="flex flex-col gap-1">
              {/* Product Section */}
              <div className="px-4 py-2">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Product
                </div>
                {productMenuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left rounded-lg px-3 py-2 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="my-2 h-px bg-slate-100 mx-4" />

              <button
                onClick={() => scrollToSection("crisis")}
                className="block rounded-xl px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium transition-colors text-left"
              >
                Why theRecruit
              </button>
              <details className="group rounded-xl px-4 py-3 text-slate-700">
                <summary className="flex cursor-pointer list-none items-center justify-between font-medium [&::-webkit-details-marker]:hidden">
                  By Industry
                  <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                </summary>
                <div className="mt-2 flex flex-col gap-1">
                  <Link
                    to="/for-legal"
                    aria-current={pathname === "/for-legal" ? "page" : undefined}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2 font-medium hover:bg-slate-50 hover:text-slate-900"
                  >
                    Legal
                  </Link>
                  <span aria-disabled="true" className="px-3 py-2 text-slate-500">
                    Hospitality <span className="ml-2 text-sm">Coming soon</span>
                  </span>
                </div>
              </details>
              <Link
                to="/pricing"
                className="block rounded-xl px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium transition-colors"
              >
                Pricing
              </Link>
              <Link
                to="/demo"
                className="block rounded-xl px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium transition-colors"
              >
                Demo Hub
              </Link>

              <div className="px-4 py-2">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Resources
                </div>
                <div className="flex flex-col gap-1">
                  {resourcesMenuItems.map((item) =>
                    item.type === "section" ? (
                      <button
                        key={item.label}
                        onClick={() => scrollToSection(item.id)}
                        className="block w-full rounded-lg px-3 py-2 text-left font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        key={item.label}
                        to={item.to}
                        className="block rounded-lg px-3 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
                      >
                        {item.label}
                      </Link>
                    )
                  )}
                </div>
              </div>

              <div className="my-3 h-px bg-slate-200" />

              {!isLoading && !isAuthenticated ? (
                <button
                  type="button"
                  onClick={() => {
                    void handleLogin();
                    setOpen(false);
                  }}
                  aria-label="Start hiring free with theRecruit"
                  className="mb-2 flex w-full items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3.5 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100"
                >
                  Start Hiring Free
                  <ChevronRight className="h-4 w-4" />
                </button>
              ) : null}

              {isLoading ? (
                <div
                  className="h-12 w-full animate-pulse rounded-xl bg-slate-100"
                  aria-hidden="true"
                  data-testid="mobile-header-auth-loading"
                />
              ) : isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setOpen(false)}
                    className="flex min-h-12 items-center gap-3 rounded-xl px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium transition-colors"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Link>
                  {planName ? (
                    <div className="mt-2 flex items-center justify-center rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                      <span className="text-sm font-semibold text-amber-700">Plan: {planName}</span>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      void handleLogout();
                    }}
                    disabled={isLoggingOut}
                    className="flex min-h-12 w-full items-center gap-3 rounded-xl px-4 py-3 text-left font-medium text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isLoggingOut ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <LogOut className="h-4 w-4" />
                    )}
                    {isLoggingOut ? "Logging out..." : "Log out"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleLogin}
                    className="block w-full text-left rounded-xl px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-medium transition-colors"
                  >
                    Login
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default GlobalHeader;
